import { useEffect, useRef } from 'react';
import { Button } from '@mui/material';
import { getStreamDisplayName } from 'src/utils/leadStreams';

import type useStreamCommand from './useStreamCommand';

export default function StreamCommandCard({
    command,
    go,
}: {
    command: ReturnType<typeof useStreamCommand>;
    go: (path: string) => void;
}) {
    const { state, prepare, confirm, cancel, check } = command;
    const card = useRef<HTMLElement>(null);
    useEffect(() => {
        if (state && state.phase !== 'loading' && state.phase !== 'saving')
            card.current?.focus({ preventScroll: true });
    }, [state?.phase]);
    if (!state) return null;
    return (
        <section
            ref={card}
            tabIndex={-1}
            className="lm-companion-command"
            aria-label="Команда для потока"
            aria-live="polite"
            aria-busy={command.busy}
        >
            {state.phase === 'select' && (
                <>
                    <p>Выберите один поток для {state.intent.active ? 'возобновления' : 'паузы'}.</p>
                    <div className="lm-companion-suggestions">
                        {state.streams.map((stream) => (
                            <button
                                key={stream.id}
                                onClick={() => void prepare(stream.id, state.intent.active)}
                            >
                                {getStreamDisplayName(stream)} · №{stream.id}
                            </button>
                        ))}
                    </div>
                </>
            )}
            {state.phase === 'loading' && <p>Проверяю поток…</p>}
            {state.phase === 'ready' && (
                <>
                    <strong>
                        {getStreamDisplayName(state.stream)} · №{state.target.id}
                    </strong>
                    {state.note && <p>{state.note}</p>}
                    <p>
                        {state.target.active
                            ? 'Возобновить поиск? Новые лиды будут расходовать пакет по его условиям.'
                            : 'Поставить поиск новых лидов на паузу?'}
                    </p>
                    <p className="lm-companion-command-note">Настройки ИИ-менеджера не меняются.</p>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={() => void confirm()}
                    >
                        {state.target.active ? 'Возобновить поток' : 'Поставить на паузу'}
                    </Button>
                </>
            )}
            {state.phase === 'saving' && (
                <>
                    <strong>
                        {state.name} · №{state.target.id}
                    </strong>
                    <p>Применяю {state.target.active ? 'возобновление' : 'паузу'} и проверяю результат…</p>
                </>
            )}
            {state.phase === 'uncertain' && (
                <>
                    <strong>
                        Поток №{state.target.id} · {state.target.active ? 'возобновление' : 'пауза'}
                    </strong>
                    <p>{state.text}</p>
                    <Button
                        variant="contained"
                        disableElevation
                        disabled={!!state.checking}
                        onClick={() => void check()}
                    >
                        {state.checking ? 'Проверяю…' : 'Проверить результат'}
                    </Button>
                    <Button onClick={() => go(`/leadstream/${state.target.id}`)}>Открыть поток</Button>
                </>
            )}
            {state.phase === 'error' && <p role="alert">{state.text}</p>}
            {state.phase !== 'saving' && state.phase !== 'uncertain' && (
                <Button onClick={cancel}>{state.phase === 'error' ? 'Закрыть' : 'Отмена'}</Button>
            )}
        </section>
    );
}
